import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DownloadSimple, ShieldCheck, Student, ChalkboardTeacher, DeviceMobile, EnvelopeSimple, Phone } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { icon: Student, t: "Students learn anywhere", d: "The complete online course runs in any browser and installs as an app — lessons, quizzes, progress tracking, and certificates. Physical skills are reinforced through optional in-person workshops." },
  { icon: ChalkboardTeacher, t: "Teachers stay in control", d: "Class rosters, progress dashboards, and a grade-visibility matrix that lets each school decide exactly which lessons each grade can see." },
  { icon: DeviceMobile, t: "Built for school devices", d: "Works on Chromebooks, iPads, and phones. No installs required, no ads, no distractions — and no student data used for anything except the training." },
];

function QuoteForm() {
  const [form, setForm] = useState({ board_name: "", contact_name: "", email: "", size: "", timeline: "", message: "" });
  const [state, setState] = useState("idle"); // idle | busy | sent | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try {
      const r = await fetch(`${API}/site/school-quote`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      setState(r.ok ? "sent" : "error");
    } catch { setState("error"); }
  };

  const inputCls = "w-full rounded-lg bg-white/5 border border-white/15 text-white text-sm px-3 py-2.5 placeholder:text-[#71717A] focus:outline-none focus:border-[#FFE01A]";
  return (
    <div id="quote-form" className="border border-[#FFE01A]/40 rounded-xl p-5 mb-12 scroll-mt-6" data-testid="quote-form">
      <h2 className="text-lg font-bold font-display mb-1" style={{ color: "#fff" }}>Request your board's quote</h2>
      <p className="text-[#A1A1AA] text-xs mb-4">We'll reply within one business day — usually with real numbers, not a sales call.</p>
      {state === "sent" ? (
        <div className="text-sm text-[#D4D4D8]" data-testid="quote-form-success">
          <b className="text-[#FFE01A]">Request received — thank you!</b> We'll be in touch within one business day.
          Want a head start? Grab the <a href={`${API}/site/privacy-package.pdf`} target="_blank" rel="noreferrer" className="text-[#FFE01A]">privacy package (PDF)</a> for your privacy officer.
        </div>
      ) : (
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
          <input data-testid="quote-board" required maxLength={200} className={inputCls} placeholder="School board / district name *" value={form.board_name} onChange={set("board_name")} />
          <input data-testid="quote-name" required maxLength={120} className={inputCls} placeholder="Your name *" value={form.contact_name} onChange={set("contact_name")} />
          <input data-testid="quote-email" required type="email" maxLength={200} className={inputCls} placeholder="Work email *" value={form.email} onChange={set("email")} />
          <select data-testid="quote-size" className={inputCls} value={form.size} onChange={set("size")} style={{ colorScheme: "dark" }}>
            <option value="">Approx. number of students…</option>
            <option>Under 500 students</option>
            <option>500 – 2,000 students</option>
            <option>2,000 – 10,000 students</option>
            <option>10,000+ students</option>
          </select>
          <select data-testid="quote-timeline" className={inputCls} value={form.timeline} onChange={set("timeline")} style={{ colorScheme: "dark" }}>
            <option value="">Timeline…</option>
            <option>This term</option>
            <option>Next school year</option>
            <option>Just exploring</option>
          </select>
          <textarea data-testid="quote-message" maxLength={2000} rows={3} className={inputCls + " sm:col-span-2"} placeholder="Anything else — pilot class, workshop interest, questions… (optional)" value={form.message} onChange={set("message")} />
          <div className="sm:col-span-2 flex items-center gap-3">
            <button data-testid="quote-submit" disabled={state === "busy"}
              className="bg-[#FFE01A] text-black font-bold text-sm px-6 py-2.5 rounded-full border-0 cursor-pointer hover:opacity-90 disabled:opacity-50">
              {state === "busy" ? "Sending…" : "Send quote request"}
            </button>
            {state === "error" && <span className="text-red-400 text-xs" data-testid="quote-form-error">Something went wrong — please check the fields and try again.</span>}
          </div>
        </form>
      )}
    </div>
  );
}

const FAQ = [
  ["What is The Self-Agency Project?", "A personal-safety and self-defense education program: a complete online course (mental skills, boundaries, awareness, legal context, and physical fundamentals) with optional in-person workshops, brought to you by Elevation650 in Alberta."],
  ["How does pricing work?", "Seats are licensed per student, per school year — that one number covers the full online course, progress tracking, quizzes, and certificates. In-person workshops and local teacher training are optional one-time add-ons, typically bundled into your first-year launch. Special package pricing is available for whole-school and multi-school board licenses — contact us for a board quote."],
  ["How do you handle student privacy (FOIP/POPA & PIPA)?", "We act as your board's service provider under Alberta's Protection of Privacy Act (POPA, formerly FOIP). We collect only name, email, and course progress; data is never sold, never used for ads or AI training; we notify boards of any incident within 72 hours; and we purge all student data with a deletion certificate at contract end. Full details on our Privacy & Schools page."],
  ["Can schools control the content by grade?", "Yes. The grade matrix lets each school show or hide every lesson per grade, and boards can lock sensitive lessons to in-person or public-only delivery."],
  ["What does onboarding look like?", "We create your board, schools, and admin accounts; teachers import their classes; students sign in with school-issued credentials. Most schools are running the first lesson within a day."],
  ["Is there a French option?", "The platform supports translated interface languages, with bilingual lesson content on our roadmap — ask us about timing for your board."],
];

export default function SchoolsPitchPage() {
  const [pricing, setPricing] = useState(null);
  const [curriculum, setCurriculum] = useState(false);
  useEffect(() => {
    document.title = "For Schools & Boards — The Self-Agency Project";
    fetch(`${API}/site/schools-pricing`).then((r) => r.json()).then(setPricing).catch(() => {});
    fetch(`${API}/site/curriculum-alignment`).then((r) => r.json()).then((d) => setCurriculum(!!d.issued)).catch(() => {});
  }, []);
  const p = pricing || {};
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-5 py-14" data-testid="schools-pitch-page">
        <Link to="/" className="text-[#FFE01A] text-sm font-bold uppercase tracking-wide no-underline">&larr; selfagency.ca</Link>
        <div className="text-xs uppercase tracking-[0.25em] text-[#A1A1AA] mt-5 mb-2">For school boards & schools · brought to you by Elevation650</div>
        <h1 className="text-4xl sm:text-5xl font-extrabold font-display mb-3" style={{ color: "#fff" }}>
          Safety skills your students keep <span className="text-[#FFE01A]">for life.</span>
        </h1>
        <p className="text-[#D4D4D8] text-base max-w-xl mb-6">
          The Self-Agency Project gives every student practical personal-safety education — online, measurable,
          and grade-appropriate — with optional in-person workshops taught by certified instructors.
        </p>
        <div className="flex flex-wrap gap-3 mb-12">
          <a href="#quote-form"
            className="inline-flex items-center gap-2 bg-[#FFE01A] text-black font-bold text-sm px-5 py-2.5 rounded-full no-underline hover:opacity-90" data-testid="pitch-quote-cta">
            <EnvelopeSimple size={16} weight="bold" /> Request a board quote
          </a>
          <a href={`${API}/site/privacy-package.pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold text-sm px-5 py-2.5 rounded-full no-underline hover:bg-white/10" data-testid="pitch-privacy-pdf">
            <DownloadSimple size={16} weight="bold" /> Privacy package (PDF)
          </a>
          {curriculum && (
            <a href={`${API}/site/curriculum-alignment.pdf`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 border border-white/25 text-white font-semibold text-sm px-5 py-2.5 rounded-full no-underline hover:bg-white/10" data-testid="pitch-curriculum-pdf">
              <DownloadSimple size={16} weight="bold" /> Curriculum alignment (PDF)
            </a>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {STEPS.map((s, i) => (
            <div key={i} className="border border-white/10 rounded-xl p-4" data-testid={`pitch-step-${i}`}>
              <s.icon size={22} weight="duotone" color="#FFE01A" />
              <div className="font-bold font-display mt-2 mb-1">{s.t}</div>
              <p className="text-[#A1A1AA] text-sm leading-relaxed m-0">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="text-lg font-bold font-display mb-3" style={{ color: "#fff" }}>Pricing</h2>
        <div className="border border-[#FFE01A]/40 rounded-xl p-5 mb-3" data-testid="pitch-pricing-seats">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-4xl font-extrabold font-display text-[#FFE01A]">{p.seat_price || "$20"}</span>
            <span className="text-[#D4D4D8] text-sm font-semibold">{p.seat_unit || "per student · per school year"}</span>
          </div>
          <ul className="text-[#D4D4D8] text-sm leading-relaxed mt-3 mb-0 pl-5 list-disc space-y-1">
            <li>Every seat includes the full online course, progress tracking, quizzes, and certificates</li>
            <li>Single-class pilots available so you can evaluate before committing</li>
          </ul>
          <p className="text-[#A1A1AA] text-xs mt-3 mb-0">{p.seat_note || "Special package pricing available for whole-school and multi-school board licenses."}</p>
          <a href="#quote-form"
            className="inline-block mt-3 text-[#FFE01A] font-bold text-sm no-underline">Get your board's quote →</a>
        </div>
        <div className="border border-white/10 rounded-xl p-5 mb-3" data-testid="pitch-pricing-launch">
          <div className="text-white font-bold font-display mb-3">One-time launch options <span className="text-[#71717A] font-normal text-xs">· typically first year</span></div>
          <div className="space-y-3">
            <div>
              <span className="text-xl font-extrabold font-display text-white">{p.workshop_price || "$500"}</span>
              <span className="text-[#D4D4D8] text-sm"> — in-person student workshop</span>
              <p className="text-[#A1A1AA] text-sm m-0">{p.workshop_detail || "per in-person workshop — covers the first 2 hours on-site, then $150/hour beyond that, plus travel expenses."}</p>
            </div>
            <div>
              <span className="text-xl font-extrabold font-display text-white">{p.local_price || "$250"}</span>
              <span className="text-[#D4D4D8] text-sm"> — local teacher training</span>
              <p className="text-[#A1A1AA] text-sm m-0">{p.local_detail || "per teacher, per day — in-person teacher training, local to the Grande Prairie area."}</p>
            </div>
          </div>
        </div>
        <p className="text-[#71717A] text-xs mb-12">Quotes are typically returned within one business day.</p>

        <QuoteForm />

        <div className="border border-[#FFE01A]/30 bg-[#FFE01A]/5 rounded-xl p-4 mb-12 flex items-start gap-3" data-testid="pitch-compliance-strip">
          <ShieldCheck size={22} weight="duotone" color="#FFE01A" className="shrink-0 mt-0.5" />
          <p className="text-[#D4D4D8] text-sm leading-relaxed m-0">
            <b className="text-white">FOIP/POPA & PIPA ready.</b> Minimal collection, no ads or AI training on student data,
            72-hour breach notification to boards, data purge with deletion certificate at contract end, and a published
            subprocessor list. Everything your privacy officer needs is on our{" "}
            <Link to="/privacy-compliance" className="text-[#FFE01A]">Privacy & Schools page</Link>.
          </p>
        </div>

        <h2 className="text-lg font-bold font-display mb-3" style={{ color: "#fff" }}>Questions boards ask us</h2>
        <div className="space-y-3 mb-12">
          {FAQ.map(([q, a], i) => (
            <details key={i} className="border border-white/10 rounded-xl p-4 group" data-testid={`pitch-faq-${i}`}>
              <summary className="font-semibold cursor-pointer list-none flex items-center justify-between">
                {q}<span className="text-[#FFE01A] group-open:rotate-45 transition-transform text-lg leading-none ml-3">+</span>
              </summary>
              <p className="text-[#A1A1AA] text-sm leading-relaxed mt-2 mb-0">{a}</p>
            </details>
          ))}
        </div>

        <div className="border-t border-white/10 pt-6 text-sm text-[#D4D4D8]">
          <div className="font-bold font-display text-white mb-1">Talk to us</div>
          <div className="flex flex-wrap gap-x-5 gap-y-1">
            <a href="mailto:admin@elevation650.com" className="text-[#FFE01A] no-underline inline-flex items-center gap-1.5"><EnvelopeSimple size={15} /> admin@elevation650.com</a>
            <a href="tel:+17802285325" className="text-[#FFE01A] no-underline inline-flex items-center gap-1.5"><Phone size={15} /> +1 780-228-5325</a>
          </div>
          <div className="text-[#71717A] text-xs mt-4">
            © {new Date().getFullYear()} Elevation650 · <Link to="/privacy" className="text-[#A1A1AA]">Privacy Policy</Link> · <Link to="/privacy-compliance" className="text-[#A1A1AA]">Privacy & Schools</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
